import type {
  AgentLaunchOptions,
  CodexHistoryEntry,
  ConversationAgent,
  ConversationPage,
  FileEntry,
  GitStatus,
  ProjectInfo,
  SessionInfo,
} from '@niuma/protocol';
import type { HubConnection } from './connection.js';

/**
 * Typed command wrappers over the raw hub connection. The hub forwards these to
 * the selected machine's runner and returns the runner's result payload.
 */
export class HubApi {
  constructor(private readonly conn: HubConnection) {}

  async listProjects(machineId: string): Promise<ProjectInfo[]> {
    const data = (await this.conn.command(machineId, { name: 'project.list' })) as {
      projects: ProjectInfo[];
    };
    return data.projects;
  }

  async createProject(
    machineId: string,
    projectName: string,
    initGit: boolean,
    addReadme: boolean,
  ): Promise<ProjectInfo> {
    const data = (await this.conn.command(machineId, {
      name: 'project.create',
      projectName,
      initGit,
      addReadme,
    })) as { project: ProjectInfo };
    return data.project;
  }

  async listSessions(machineId: string): Promise<SessionInfo[]> {
    const data = (await this.conn.command(machineId, { name: 'session.list' })) as {
      sessions: SessionInfo[];
    };
    return data.sessions;
  }

  async createSession(
    machineId: string,
    agent: SessionInfo['agent'],
    project: string | undefined,
    cols: number,
    rows: number,
    options?: AgentLaunchOptions,
  ): Promise<SessionInfo> {
    const data = (await this.conn.command(machineId, {
      name: 'session.create',
      agent,
      project,
      cols,
      rows,
      options,
    })) as { session: SessionInfo };
    return data.session;
  }

  async stopSession(machineId: string, sessionId: string): Promise<void> {
    await this.conn.command(machineId, { name: 'session.stop', sessionId });
  }

  async sessionOutput(
    machineId: string,
    sessionId: string,
    tailBytes?: number,
  ): Promise<Uint8Array> {
    const data = (await this.conn.command(machineId, {
      name: 'session.output',
      sessionId,
      tailBytes,
    })) as { dataB64: string };
    return Uint8Array.from(atob(data.dataB64), (char) => char.charCodeAt(0));
  }

  async conversationPage(
    machineId: string,
    agent: ConversationAgent,
    transcriptId: string,
    cwd: string,
    beforeCursor?: string,
    limit = 40,
  ): Promise<ConversationPage> {
    return (await this.conn.command(machineId, {
      name: 'conversation.page',
      agent,
      transcriptId,
      cwd,
      beforeCursor,
      limit,
    })) as ConversationPage;
  }

  async uploadAttachment(
    machineId: string,
    sessionId: string,
    fileName: string,
    mimeType: string,
    dataB64: string,
  ): Promise<{ path: string; relativePath: string }> {
    return (await this.conn.command(machineId, {
      name: 'session.attachment',
      sessionId,
      fileName,
      mimeType,
      dataB64,
    })) as { path: string; relativePath: string };
  }

  async codexHistory(
    machineId: string,
    project: string | undefined,
    limit = 25,
  ): Promise<CodexHistoryEntry[]> {
    const data = (await this.conn.command(machineId, {
      name: 'codex.history',
      project,
      limit,
    })) as { sessions: CodexHistoryEntry[] };
    return data.sessions;
  }

  async resumeCodex(
    machineId: string,
    codexSessionId: string,
    cwd: string,
    project: string | undefined,
    cols: number,
    rows: number,
    options?: AgentLaunchOptions,
  ): Promise<SessionInfo> {
    const data = (await this.conn.command(machineId, {
      name: 'codex.resume',
      codexSessionId,
      cwd,
      project,
      cols,
      rows,
      options,
    })) as { session: SessionInfo };
    return data.session;
  }

  async sendInput(machineId: string, sessionId: string, dataB64: string): Promise<void> {
    await this.conn.command(machineId, { name: 'session.input', sessionId, dataB64 });
  }

  async resize(machineId: string, sessionId: string, cols: number, rows: number): Promise<void> {
    await this.conn.command(machineId, { name: 'session.resize', sessionId, cols, rows });
  }

  async tree(machineId: string, project: string, relativePath: string): Promise<FileEntry[]> {
    const data = (await this.conn.command(machineId, {
      name: 'workspace.tree',
      project,
      relativePath,
    })) as { entries: FileEntry[] };
    return data.entries;
  }

  async gitStatus(machineId: string, project: string): Promise<GitStatus> {
    const data = (await this.conn.command(machineId, { name: 'workspace.git', project })) as {
      status: GitStatus;
    };
    return data.status;
  }

  async diff(machineId: string, project: string): Promise<string> {
    const data = (await this.conn.command(machineId, { name: 'workspace.diff', project })) as {
      diff: string;
    };
    return data.diff;
  }
}
